// Inventário de rastreadores (`/trackers`). Regra PURA (camada 3a): validação
// do formulário, espelho da do backend (`TrackerEndpoints`), e o estado da
// lista na URL. A página só desenha.
//
// O backend é quem decide: validar aqui é pra apontar o campo errado antes do
// round-trip, não pra substituir o 400 do servidor.

import type { TrackerStatus } from '@/api/trackers';

export const TRACKERS_PAGE_SIZE = 25;

/// IMEI: 15 dígitos, o último é o verificador Luhn. Mesma regra de
/// `TrackerEndpoints.IsValidImei`.
export function isValidImei(imei: string): boolean {
  if (!/^\d{15}$/.test(imei)) return false;
  let sum = 0;
  for (let i = 0; i < 15; i++) {
    let d = imei.charCodeAt(i) - 48;
    if (i % 2 === 1) {
      d *= 2;
      if (d > 9) d -= 9;
    }
    sum += d;
  }
  return sum % 10 === 0;
}

/// O que o campo IMEI guarda do que foi digitado ou colado: só dígitos, até
/// 15. O corte vem DEPOIS de tirar o resto: com `maxLength` no input, o
/// navegador cortava o texto colado antes, e um IMEI agrupado com espaços
/// ("49015 42032 37518") perdia os últimos dígitos.
export function imeiInput(raw: string): string {
  return raw.replace(/\D/g, '').slice(0, 15);
}

/// ICCID do chip: 19 ou 20 dígitos, o último é o verificador Luhn (E.118).
/// Mesma regra de `Iccid.IsValid` no backend. Os chips Vivo começam com 8955,
/// mas o prefixo não é exigido (chip M2M estrangeiro seria recusado).
export function isValidIccid(iccid: string): boolean {
  if (!/^\d{19,20}$/.test(iccid)) return false;
  let sum = 0;
  for (let i = 0; i < iccid.length; i++) {
    let d = iccid.charCodeAt(iccid.length - 1 - i) - 48;
    if (i % 2 === 1) {
      d *= 2;
      if (d > 9) d -= 9;
    }
    sum += d;
  }
  return sum % 10 === 0;
}

/// O que o campo ICCID guarda do que foi digitado ou colado: só dígitos, até
/// 20. Mesmo motivo do `imeiInput`: o cartão do chip imprime o número
/// agrupado com espaços, e cortar antes de limpar perderia o fim.
export function iccidInput(raw: string): string {
  return raw.replace(/\D/g, '').slice(0, 20);
}

/// Nº de série como o backend grava: sem espaço e em maiúsculas.
export function normalizeSerial(serial: string): string {
  return serial.replace(/\s+/g, '').toUpperCase();
}

export const MAX_SERIAL_LENGTH = 64;

export interface TrackerFormValues {
  trackerModelId: string;
  imei: string;
  serialNumber: string;
  /// Opcional: vazio = sem chip.
  iccid: string;
  /// `yyyy-MM-dd`, como o `<input type="date">` entrega.
  purchaseDate: string;
}

export type TrackerFormField = keyof TrackerFormValues;

/// Erros por campo. `today` em `yyyy-MM-dd` (data local de quem preenche).
export function validateTrackerForm(
  v: TrackerFormValues,
  today: string,
): Partial<Record<TrackerFormField, string>> {
  const errors: Partial<Record<TrackerFormField, string>> = {};
  if (!v.trackerModelId) errors.trackerModelId = 'Escolha o modelo.';
  const imei = v.imei.trim();
  if (!imei) errors.imei = 'Informe o IMEI.';
  else if (!/^\d{15}$/.test(imei)) errors.imei = 'O IMEI tem 15 dígitos.';
  else if (!isValidImei(imei)) errors.imei = 'IMEI inválido: confira os dígitos na etiqueta.';
  const serial = normalizeSerial(v.serialNumber);
  if (!serial) errors.serialNumber = 'Informe o número de série.';
  else if (serial.length > MAX_SERIAL_LENGTH) errors.serialNumber = `Máximo de ${MAX_SERIAL_LENGTH} caracteres.`;
  const iccid = v.iccid.trim();
  if (iccid) {
    if (!/^\d{19,20}$/.test(iccid)) errors.iccid = 'O ICCID tem 19 ou 20 dígitos.';
    else if (!isValidIccid(iccid)) errors.iccid = 'ICCID inválido: confira os dígitos impressos no chip.';
  }
  if (!/^\d{4}-\d{2}-\d{2}$/.test(v.purchaseDate)) errors.purchaseDate = 'Informe a data da compra.';
  else if (v.purchaseDate > today) errors.purchaseDate = 'A data da compra não pode ser no futuro.';
  return errors;
}

/// Código de erro do backend → campo e mensagem. Código desconhecido cai no
/// erro geral do formulário (`field: null`). `heldByImei`/`heldByStatus` vêm
/// no 409 de ICCID: é o rastreador que está com o chip. Com ele inativo, a
/// mensagem diz como achá-lo, porque a lista abre em "Ativos" e a busca pelo
/// IMEI voltaria vazia (review do #1247).
export function serverErrorMessage(
  code: string | null | undefined,
  heldByImei?: string | null,
  heldByStatus?: string | null,
): {
  field: TrackerFormField | null;
  message: string;
} {
  switch (code) {
    case 'duplicate_iccid': {
      const exit = 'Se ele mudou de aparelho, apague o ICCID daquele rastreador e tente de novo.';
      if (!heldByImei) {
        return {
          field: 'iccid',
          message:
            'Este chip já está em outro rastreador (inclusive entre os inativos). Se ele mudou de aparelho, apague o ICCID de lá e tente de novo.',
        };
      }
      const where =
        heldByStatus === 'inactive'
          ? `Este chip já está no rastreador de IMEI ${heldByImei}, que está inativo (para achá-lo, use o filtro Status: Todos).`
          : `Este chip já está no rastreador de IMEI ${heldByImei}.`;
      return { field: 'iccid', message: `${where} ${exit}` };
    }
    case 'invalid_iccid':
      return { field: 'iccid', message: 'ICCID inválido: confira os dígitos impressos no chip.' };
    case 'duplicate_imei':
      // A saída vai junto: o caso realista é outro aparelho cadastrado com o
      // IMEI desta caixa, e ele se corrige na edição dele.
      return {
        field: 'imei',
        message:
          'Já existe um rastreador com este IMEI (inclusive entre os inativos). Se ele foi cadastrado com o IMEI errado, corrija o IMEI dele e tente de novo.',
      };
    case 'duplicate_serial':
      return { field: 'serialNumber', message: 'Já existe um rastreador deste modelo com este número de série.' };
    case 'invalid_imei':
      return { field: 'imei', message: 'IMEI inválido: confira os dígitos na etiqueta.' };
    case 'invalid_serial':
      return { field: 'serialNumber', message: 'Número de série inválido.' };
    case 'invalid_purchase_date':
      return { field: 'purchaseDate', message: 'Data da compra inválida ou no futuro.' };
    case 'unknown_model':
      return { field: 'trackerModelId', message: 'Modelo não encontrado no catálogo.' };
    default:
      return { field: null, message: 'Não foi possível salvar o rastreador. Tente novamente.' };
  }
}

// ─── Estado da lista na URL ─────────────────────────────────────────

/// `''` = todos os status.
export type StatusFilter = TrackerStatus | '';

export interface TrackerListState {
  manufacturer: string;
  modelId: string;
  status: StatusFilter;
  q: string;
  page: number;
}

/// Sem `status` na URL = "ativos": o inventário que importa no dia a dia é o
/// que está em uso. "Todos" vira `status=all` explícito.
export function readListState(params: URLSearchParams): TrackerListState {
  const rawStatus = params.get('status');
  const status: StatusFilter =
    rawStatus === 'all' ? '' : rawStatus === 'inactive' ? 'inactive' : 'active';
  const page = Number.parseInt(params.get('page') ?? '', 10);
  return {
    manufacturer: params.get('manufacturer') ?? '',
    modelId: params.get('modelId') ?? '',
    status,
    q: params.get('q') ?? '',
    page: Number.isFinite(page) && page > 0 ? page : 1,
  };
}

/// Inverso do `readListState`, omitindo o que é default (URL curta).
export function writeListState(s: TrackerListState): URLSearchParams {
  const p = new URLSearchParams();
  if (s.manufacturer) p.set('manufacturer', s.manufacturer);
  if (s.modelId) p.set('modelId', s.modelId);
  if (s.status === '') p.set('status', 'all');
  else if (s.status === 'inactive') p.set('status', 'inactive');
  if (s.q.trim()) p.set('q', s.q.trim());
  if (s.page > 1) p.set('page', String(s.page));
  return p;
}

/// A navegação que acabou de acontecer é de FORA da tela (link do menu,
/// voltar/avançar) e deve reler a URL? O espelho da tela só escreve com
/// `replace`, então `REPLACE` é dele; `PUSH` e `POP` não são. A navegação da
/// própria montagem também não conta. Comparar com a última URL escrita não
/// serve: no RR7 a URL chega atrasada (`traps.md`), e uma escrita velha do
/// próprio espelho contaria como externa.
export function shouldRereadUrl(
  locationKey: string,
  mountKey: string,
  navigationType: 'POP' | 'PUSH' | 'REPLACE',
): boolean {
  return locationKey !== mountKey && navigationType !== 'REPLACE';
}

/// Query string do `GET /api/admin/trackers`.
export function listQuery(s: TrackerListState, pageSize = TRACKERS_PAGE_SIZE): string {
  const p = new URLSearchParams();
  if (s.manufacturer) p.set('manufacturer', s.manufacturer);
  if (s.modelId) p.set('modelId', s.modelId);
  if (s.status) p.set('status', s.status);
  if (s.q.trim()) p.set('q', s.q.trim());
  p.set('page', String(s.page));
  p.set('pageSize', String(pageSize));
  return p.toString();
}

export function pageCount(total: number, pageSize: number): number {
  return Math.max(1, Math.ceil(total / pageSize));
}

/// "26–50 de 73". Lista vazia → "0 de 0".
export function rangeLabel(page: number, pageSize: number, shown: number, total: number): string {
  if (shown === 0) return `0 de ${total}`;
  const from = (page - 1) * pageSize + 1;
  return `${from}–${from + shown - 1} de ${total}`;
}

/// `2026-09-01` → `01/09/2026`. Sem `Date`: a data é de calendário, e passar
/// por `new Date('2026-09-01')` (meia-noite UTC) exibiria 31/08 em Fortaleza.
export function formatPurchaseDate(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : iso;
}

/// Hoje em `yyyy-MM-dd` no relógio local do navegador.
export function todayIso(now: Date = new Date()): string {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/// Fabricantes distintos do catálogo, em ordem.
export function manufacturersOf(models: readonly { manufacturer: string }[]): string[] {
  return [...new Set(models.map((m) => m.manufacturer))].sort((a, b) => a.localeCompare(b, 'pt-BR'));
}
